import React from 'react'
import { ClientCall, CallStatus, VoiceTranscriptRole } from '@numeo/types'
import { Box, Stack, Typography, TypographySystem } from '@mui/joy'
import { convertVoiceAgentToolCallToMessage } from 'utils/convert-agent-tool-call-to-message'

interface AICallChatProps {
    call: ClientCall
    fullHeight?: boolean
    showHeader?: boolean
}

export const AICallChat: React.FC<AICallChatProps> = ({ call, fullHeight = false, showHeader = true }) => {
    // Helper function to get role-specific styling for transcripts
    const getRoleStyles = (role: string) => {
        switch (role) {
            case VoiceTranscriptRole.ASSISTANT:
                return {
                    justifyContent: 'flex-end',
                    bgcolor: 'primary.softBg',
                    borderColor: 'primary.outlinedBorder',
                    textColor: 'primary.plainColor',
                    label: '🤖 Jack (AI):',
                    fontWeight: 'md',
                    maxWidth: '85%',
                    borderRadius: 'lg',
                    borderStyle: 'solid',
                    opacity: 1,
                    fontStyle: 'normal',
                    fontSize: 'body-sm',
                }
            case VoiceTranscriptRole.TOOL:
                return {
                    justifyContent: 'flex-end',
                    bgcolor: 'warning.softBg',
                    borderColor: 'warning.outlinedBorder',
                    textColor: 'warning.plainColor',
                    label: '🔧 AI Tool:',
                    fontWeight: 'md',
                    maxWidth: '95%',
                    borderRadius: 'sm',
                    borderStyle: 'dashed',
                    opacity: 0.9,
                    fontStyle: 'italic',
                    fontSize: 'sm',
                }
            case VoiceTranscriptRole.USER:
                return {
                    justifyContent: 'flex-start',
                    bgcolor: 'neutral.softBg',
                    borderColor: 'neutral.outlinedBorder',
                    textColor: 'neutral.plainColor',
                    label: '👤 Broker:',
                    fontWeight: 'normal',
                    maxWidth: '85%',
                    borderRadius: 'lg',
                    borderStyle: 'solid',
                    opacity: 1,
                    fontStyle: 'normal',
                    fontSize: 'body-sm',
                }
            case VoiceTranscriptRole.DISPATCHER:
                return {
                    justifyContent: 'flex-end',
                    bgcolor: 'success.softBg',
                    borderColor: 'success.outlinedBorder',
                    textColor: 'success.plainColor',
                    label: '🎧 Dispatcher:',
                    fontWeight: 'md',
                    maxWidth: '85%',
                    borderRadius: 'lg',
                    borderStyle: 'solid',
                    opacity: 1,
                    fontStyle: 'normal',
                    fontSize: 'body-sm',
                }
            default:
                console.warn('Unknown voice transcript role:', role)
                return {}
        }
    }

    return (
        <Stack direction={'column'} spacing={1} sx={{ mt: call.recordURL ? 2 : 0 }}>
            {call?.transcripts?.length ? (
                <>
                    {showHeader && (
                        <Typography level="body-sm" sx={{ fontWeight: 'medium', mb: 1 }}>
                            Conversation Transcript:
                        </Typography>
                    )}
                    {/* Transcript container with nested scrolling */}
                    <Box
                        sx={{
                            height: fullHeight ? '100%' : 'auto',
                            maxHeight: fullHeight ? 'none' : '250px',
                            overflow: 'auto',
                            p: 1,
                            bgcolor: 'background.surface',
                            borderRadius: 'md',
                            '&::-webkit-scrollbar': {
                                width: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                                bgcolor: 'background.level1',
                                borderRadius: '3px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: 'neutral.500',
                                borderRadius: '3px',
                                '&:hover': {
                                    backgroundColor: 'neutral.600',
                                },
                            },
                        }}
                    >
                        {call.transcripts.map((transcript, index) => {
                            const roleStyles = getRoleStyles(transcript.role)
                            return (
                                <Box
                                    key={`${transcript.role}-${index}`}
                                    sx={{
                                        mb: 1.5,
                                        display: 'flex',
                                        justifyContent: roleStyles.justifyContent,
                                        width: '100%',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            bgcolor: roleStyles.bgcolor,
                                            px: 2,
                                            py: 1,
                                            borderRadius: roleStyles.borderRadius,
                                            maxWidth: roleStyles.maxWidth,
                                            boxShadow: 'sm',
                                            border: '1px solid',
                                            borderColor: roleStyles.borderColor,
                                            borderStyle: roleStyles.borderStyle,
                                            opacity: roleStyles.opacity,
                                        }}
                                    >
                                        <Typography
                                            level={roleStyles.fontSize as keyof TypographySystem}
                                            sx={{
                                                color: 'text.primary',
                                                fontWeight: roleStyles.fontWeight,
                                                fontStyle: roleStyles.fontStyle,
                                                wordBreak: 'break-word',
                                                '& strong': {
                                                    color: roleStyles.textColor,
                                                    mr: 0.5,
                                                },
                                            }}
                                        >
                                            <strong>{roleStyles.label}</strong>
                                            {transcript.tool ? convertVoiceAgentToolCallToMessage(transcript.tool) : transcript.content}
                                        </Typography>
                                    </Box>
                                </Box>
                            )
                        })}
                    </Box>
                </>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        py: 2,
                    }}
                >
                    <Box
                        sx={{
                            bgcolor: 'background.level1',
                            px: 2,
                            py: 1,
                            borderRadius: 'md',
                            width: '80%',
                            boxShadow: 'sm',
                            border: '1px dashed',
                            borderColor: 'neutral.300',
                        }}
                    >
                        <Typography level="body-sm" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                            {[CallStatus.CONNECTED, CallStatus.INITIATING].includes(call.status)
                                ? 'Waiting for conversation transcript...'
                                : 'No transcript available for this call'}
                        </Typography>
                    </Box>
                </Box>
            )}
        </Stack>
    )
}
